import React, { useEffect, useState } from 'react';
import AutosizeInput from 'react-input-autosize';
import styled, { css } from 'styled-components';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Colors, usePrompt } from '../../../ui';
import { useResolve } from '../../Container';
import { useEditorSessionObserver } from '../EditorSession';
import { Selection } from '../Selection';
import { setSizes } from '../commands/TransformCommands';
import { } from '../entities/Physical';
import { Resizable } from '../entities/Resizable';

const XAutosizeInput = AutosizeInput as any;

const Frame = styled.div`
`;

const InputFrame = styled.div<{ active: boolean }>`
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translate(-50%, 0);
    
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: .25rem;
    padding: .0625rem .375rem 0;
    border: .125rem solid transparent;
    border-radius: .25rem;
    cursor: pointer;
    border: .125rem solid transparent;

    & > * {
        pointer-events: none;
    }

    &:hover {
        border: .125rem solid ${Colors.primaryLight};
    }

    ${p => p.active && css`
        border: .125rem solid ${Colors.primaryLight};
        cursor: initial;

        & > * {
            pointer-events: all;
        }
    `}

    span {
        color: ${Colors.primaryLight};
        font-size: 1rem;
        font-weight: 450;
        margin-right: .125rem;
    }
`;

const InputBorder = styled.div`
    input {
        border: none;
        padding: none;
        margin: none;
        background: none;

        color: ${Colors.primaryLight};
        font-size: 1rem;
        font-weight: 450;

        &:after {
            content: '';
            position: absolute;
            inset: 0;
            border: 1px solid ${Colors.primaryLight};
        }
    }
`;

type InputButtonProps = {
    autoFocus?: boolean,
    enabled?: boolean,
    value: any,
    onChange: (x: string) => void,
};

const InputButton = ({ autoFocus, enabled, value, onChange }: InputButtonProps) => {

    const onInput = (e: any) => {
        onChange(e.target.value);
    };

    return <InputBorder>
        <XAutosizeInput
            key={enabled.toString()} // This makes autoFocus work
            autoFocus={enabled && autoFocus}
            value={value}
            onChange={onInput} />
    </InputBorder>;
};

const useSubject = () => {
    const selection = useEditorSessionObserver(Selection);
    const subject = selection.firstAndOnly()?.as(Resizable);
    if (subject?.alive) return subject;
    return null;
};

export const SizeWidget = () => {

    const dispatch = useResolve(CommandDispatcher);
    const subject = useSubject();

    const [editing, setEditing] = useState(false);
    const [width, setWidth] = useState(`${Math.round(subject?.width)}`);
    const [height, setHeight] = useState(`${Math.round(subject?.height)}`);

    useEffect(() => {
        if (!editing && !!subject) {
            setWidth(`${Math.round(subject?.width)}`);
            setHeight(`${Math.round(subject?.height)}`);
        }
    }, [editing, subject?.ticker]);

    const onApply = (confirm?: boolean) => {
        if (confirm) {
            dispatch(setSizes, [width, height]);
        }
        setWidth(`${Math.round(subject.width)}`);
        setHeight(`${Math.round(subject.height)}`);
        setEditing(false);
    };

    const promptRef = usePrompt({
        disabled: !editing,
        clickOffIsPositive: true,
        onAction: onApply,
    });

    if (!subject) return null;

    return <Frame>
        <InputFrame ref={promptRef} active={editing} onClick={() => setEditing(!!subject)}>
            <InputButton
                autoFocus
                enabled={editing}
                value={width}
                onChange={x => setWidth(x)} />
            <span>{'×'}</span>
            <InputButton
                enabled={editing}
                value={height}
                onChange={x => setHeight(x)} />
        </InputFrame>
    </Frame>;
};
